/**
 * Small presentational building blocks for the dashboard: KPI cards and panels.
 * Kept dumb — all data shaping lives in src/lib/reporting.ts (and in SQL).
 */

type Tone = 'good' | 'bad' | 'neutral';

export function KpiCard({
  label,
  value,
  sub,
  tone = 'neutral',
  accent,
  delta,
}: {
  label: string;
  value: string;
  sub?: string;
  tone?: Tone;
  /** Top accent stripe colour. */
  accent?: string;
  /** Optional period-over-period change chip (label + tone). */
  delta?: { label: string; tone: Tone } | null;
}) {
  return (
    <div className="kpi-card" style={accent ? { borderTopColor: accent } : undefined}>
      <div className="kpi-label">{label}</div>
      <div className="kpi-value">{value}</div>
      {delta && (
        <div className={`kpi-delta kpi-delta--${delta.tone}`}>
          <span aria-hidden="true">{delta.tone === 'good' ? '▲' : delta.tone === 'bad' ? '▼' : '■'}</span>{' '}
          {delta.label} <span className="kpi-delta-note">vs prev</span>
        </div>
      )}
      {sub && <div className={`kpi-sub kpi-sub--${tone}`}>{sub}</div>}
    </div>
  );
}

export function Panel({
  title,
  subtitle,
  action,
  children,
}: {
  title: string;
  subtitle?: string;
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section className="panel">
      <div className="panel-head">
        <div>
          <h2 className="panel-title">{title}</h2>
          {subtitle && <p className="panel-subtitle">{subtitle}</p>}
        </div>
        {action && <div className="panel-action">{action}</div>}
      </div>
      <div className="panel-body">{children}</div>
    </section>
  );
}
